import { useSnapshot } from 'valtio'
import { ProfileStoreContext, useProfileStore } from './ProfileStore.ts'
import { useLazyRef } from '../../shared/lib/hooks.ts'
import { useAppModule } from '../appContext.ts'
import { ProfileManageAccount } from './ProfileManageAccount.tsx'
import { ProfileOnboardingInfo } from './ProfileOnboardingInfo.tsx'
import {
  ProfileSubscription,
  PostChangeSubscription,
} from './ProfileSubscription.tsx'
import { ProfileSupport } from './ProfileSupport.tsx'
import { ProfileAbout } from './ProfileAbout.tsx'
import { getValueByKey } from '../../shared/lib/utils.ts'
import { FormattedMessage } from 'react-intl'
import { OnboardingData } from '../onboarding/onboardingData.ts'
import {
  ProfileButton,
  ProfileDrawer,
  ProfileMenuBlock,
  ProfileTitle,
} from './ProfileComponents.tsx'
import { Button } from '../../shared/ui/button/button.tsx'
import { useNavigate, useParams } from 'react-router-dom'
import { urls } from '../../shared/urls.ts'
import { ProfileSections } from '../../shared/data/profile.ts'
import { BuyStoreContext } from '../onboarding/buyStore.ts'
import { ProfileCancelSubscription } from './ProfileCancelSubscription.tsx'
import { ProfileNotifications } from './ProfileNotifications.tsx'

export const ProfileView = (props: { onboardingData: OnboardingData }) => {
  const appModule = useAppModule()
  const store = useLazyRef(() =>
    appModule.profileStore(props.onboardingData),
  ).current
  const params = useParams()
  const buyStore = useLazyRef(() => appModule.buyStore('ru')).current

  const componentMap = {
    manageAccount: <ProfileManageAccount />,
    changeOnboardingInfo: <ProfileOnboardingInfo />,
    manageSubscription: <ProfileSubscription />,
    postChangeSubscription: <PostChangeSubscription />,
    support: <ProfileSupport />,
    notifications: <ProfileNotifications />,
    // appLanguage: <ProfileAppLanguage />,
    about: <ProfileAbout />,
    cancelSubscription: <ProfileCancelSubscription />,
  }

  const content = getValueByKey(componentMap, params.section) ?? (
    <ProfileMainMenu />
  )

  return (
    <BuyStoreContext.Provider value={buyStore}>
      <ProfileStoreContext.Provider value={store}>
        <div className="mx-auto w-full max-w-800 flex-1 p-16 pb-100 xl:pb-0 2xl:max-w-1200 2xl:p-32 2xl:text-18">
          {content}
        </div>
      </ProfileStoreContext.Provider>
    </BuyStoreContext.Provider>
  )
}
function getMenuImage(image: ProfileSections | 'logout') {
  return `/images/profile/${image}.svg`
}

function ProfileMainMenu() {
  const store = useProfileStore()
  const state = useSnapshot(store.state)
  const navigate = useNavigate()

  return (
    <div className="flex flex-col">
      <ProfileTitle
        hideBack
        title={<FormattedMessage id="user.personalProfile" />}
      />
      <div className="flex flex-col gap-16">
        <ProfileMenuBlock>
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.ManageAccount)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.ManageAccount))
            }}
            title={<FormattedMessage id="user.manageAccount" />}
            subtitle={state.email}
          />
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.ChangeOnboardingInfo)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.ChangeOnboardingInfo))
            }}
            title={<FormattedMessage id="user.title.changeOnboardingInfo" />}
          />
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.ManageSubscription)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.ManageSubscription))
            }}
            title={<FormattedMessage id="user.manageSubscription" />}
            // subtitle={
            //   state.subscription && (
            //     <FormattedMessage
            //       id="months"
            //       values={{ count: planInfos[state.subscription.plan].month }}
            //     />
            //   )
            // }
          />
        </ProfileMenuBlock>

        <ProfileMenuBlock>
          {/* <ProfileButton
            imageUrl={getMenuImage(ProfileSections.Faq)}
            onClick={() =>
              window.open('https://img.edman.ai/pdf/edman_faq.pdf')
            }
            title={<FormattedMessage id="faq" />}
          /> */}
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.Support)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.Support))
            }}
            title={<FormattedMessage id="user.support" />}
          />
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.Notifications)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.Notifications))
            }}
            title={<FormattedMessage id="user.notifications" />}
          />
          {/* <ProfileButton
            imageUrl={getMenuImage(ProfileSections.AppLanguage)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.AppLanguage))
            }}
            title={<FormattedMessage id="user.title.appLang" />}
            subtitle={appState.language == 'en' ? 'English' : 'Русский'}
          /> */}
          {/* <ProfileButton
            imageUrl={getMenuImage(ProfileSections.About)}
            onClick={() => {
              navigate(urls.profile(ProfileSections.About))
            }}
            title={<FormattedMessage id="user.aboutUs" />}
            subtitle={
              state.version ? (
                <FormattedMessage
                  id="version"
                  values={{ version: state.version }}
                />
              ) : (
                <></>
              )
            }
          /> */}
        </ProfileMenuBlock>

        <ProfileMenuBlock>
          <ProfileButton
            imageUrl={getMenuImage(ProfileSections.Logout)}
            onClick={() => {
              store.setShowLogout(true)
            }}
            title={<FormattedMessage id="Log out" />}
          />
        </ProfileMenuBlock>
      </div>
      <LogoutDrawer />
    </div>
  )
}

function LogoutDrawer() {
  const store = useProfileStore()
  const authStore = useAppModule().authStore
  const state = useSnapshot(store.state)
  return (
    <ProfileDrawer
      title={<FormattedMessage id="Log out?" />}
      open={state.showLogout}
    >
      <div className="flex flex-col gap-16">
        <div>
          <FormattedMessage id="areYouSureLogout" />
        </div>

        <Button
          rounded="full"
          size="large"
          bg="red-gradient"
          onClick={() => {
            authStore.logoutAndRefresh()
          }}
        >
          <FormattedMessage id="Log out" />
        </Button>

        <Button
          bg="transparent"
          size="large"
          onClick={() => {
            store.closeDrawer()
          }}
        >
          <FormattedMessage id="cancel" />
        </Button>
      </div>
    </ProfileDrawer>
  )
}
